import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { heroData } from '../mock';

const KineticHero = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const lineVariants = {
    hidden: { y: 100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, 0.05, 0.01, 0.9],
      },
    },
  };

  const imageVariants = {
    hidden: { scale: 1.2, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 1.5,
        ease: [0.6, 0.05, 0.01, 0.9],
      },
    },
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Animated background grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f15_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f15_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      
      {/* Gradient orbs */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      />

      <div className="relative z-10 container mx-auto px-6 md:px-12 py-32">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isLoaded ? "visible" : "hidden"}
          className="max-w-6xl mx-auto text-center"
        >
          {/* Small badge */}
          <motion.div variants={lineVariants} className="mb-8 flex justify-center">
            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              <span className="relative flex h-2 w-2 mr-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              Vamos Digital Solusi - Partner Resmi WhaTap
            </span>
          </motion.div>

          {/* Title with line-by-line reveal */}
          <div className="overflow-hidden mb-6">
            <motion.h1
              variants={lineVariants}
              className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-tight"
            >
              {heroData.title}
            </motion.h1>
          </div>

          <div className="overflow-hidden mb-6">
            <motion.h2
              variants={lineVariants}
              className="text-3xl md:text-5xl font-semibold bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent"
            >
              {heroData.subtitle}
            </motion.h2>
          </div>

          <div className="overflow-hidden mb-12">
            <motion.p
              variants={lineVariants}
              className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed"
            >
              {heroData.description}
            </motion.p>
          </div>

          {/* CTA Buttons */}
          <motion.div
            variants={lineVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(16, 185, 129, 0.3)" }}
                whileTap={{ scale: 0.95 }}
                className="group px-8 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-full font-semibold text-lg flex items-center gap-2 shadow-lg shadow-emerald-500/25 transition-all duration-300"
              >
                {heroData.cta.primary}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </Link>

            <motion.a
              href="https://service.whatap.io/account/create?_gl=1*t6k6li*_gcl_au*MTQ4NjMyMjgwMS4xNzg0MTY5MzY5*_ga*MjU1MDA5NDI4LjE3NjE3OTgyNzI.*_ga_MR3V3MJVJ2*czE3ODQxNjkzNjkkbzExJGcxJHQxNzg0MTY5Mzg3JGo0MiRsMCRoMA..*_ga_NR722KYGXH*czE3ODQxNjkzNjkkbzExJGcwJHQxNzg0MTY5MzY5JGo2MCRsMCRoMA.."
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="group px-8 py-4 bg-white/5 backdrop-blur-sm border border-white/10 text-white rounded-full font-semibold text-lg flex items-center gap-2 hover:bg-white/10 transition-all duration-300"
            >
              <Play className="w-5 h-5" />
              {heroData.cta.secondary}
            </motion.a>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            variants={lineVariants}
            className="mt-24"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="flex flex-col items-center gap-2 text-slate-400"
            >
              <span className="text-sm">Scroll untuk melihat lebih</span>
              <div className="w-6 h-10 border-2 border-slate-400 rounded-full p-1">
                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-1.5 h-3 bg-slate-400 rounded-full mx-auto"
                />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Decorative elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-950 to-transparent" />
    </section>
  );
};

export default KineticHero;
